import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/api";
import "./stylesheet/AdminEvents.css";
import logoSvg from "../assets/logo.svg";

const RESOURCES_MAP = [
  { id: "projetor", label: "Projetor" },
  { id: "som", label: "Sistema de Som" },
  { id: "microfone", label: "Microfone" },
  { id: "notebook", label: "Notebook" },
  { id: "outros", label: "Outros" },
];

const STATUS_TABS = [
  { key: "Pendente", label: "Pendentes" },
  { key: "Aprovado", label: "Aprovados" },
  { key: "Concluído", label: "Concluídos" },
  { key: "Cancelado", label: "Cancelados" },
];

const normalizeStatus = (status = "") =>
  status
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Pendente");

  const [viewEvent, setViewEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [technician, setTechnician] = useState("");
  const [status, setStatus] = useState("Aprovado");

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  /* =============================
     LOAD EVENTS
  ============================== */
  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("tickets/listEvents.php");

      if (res.data?.success && Array.isArray(res.data.data)) {
        const unique = Array.from(
          new Map(res.data.data.map(e => [e.id, e])).values()
        );
        setEvents(unique);
      } else {
        setEvents([]);
      }
    } catch {
      setErrorMessage("Erro ao carregar eventos do servidor.");
      setShowError(true);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  /* =============================
     FILTER
  ============================== */
  const filteredEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return events.filter(e =>
      normalizeStatus(e.status || "Pendente") === normalizeStatus(activeTab)
    );
  }, [events, activeTab]);

  /* =============================
     UPDATE
  ============================== */
  async function handleUpdateEvent() {
    if (!technician.trim()) {
      setErrorMessage("Informe o técnico responsável.");
      setShowError(true);
      return;
    }

    try {
      const res = await api.post("tickets/updateEventStatus.php", {
        id: selectedEvent.id,
        technician,
        status,
      });

      if (res.data?.success) {
        await loadEvents();
        setSelectedEvent(null);
      } else {
        setErrorMessage(res.data.message || "Erro ao atualizar evento.");
        setShowError(true);
      }
    } catch {
      setErrorMessage("Erro de comunicação com o servidor.");
      setShowError(true);
    }
  }

  /* =============================
     DELETE
  ============================== */
  async function handleDelete(id) {
    if (!window.confirm("Deseja realmente excluir este evento?")) return;

    try {
      const formData = new FormData();
      formData.append("id", id);

      const res = await api.post("tickets/deleteEvent.php", formData);
      if (res.data?.success) await loadEvents();
      else {
        setErrorMessage(res.data.message || "Erro ao excluir evento.");
        setShowError(true);
      }
    } catch {
      setErrorMessage("Erro ao excluir evento.");
      setShowError(true);
    }
  }
  const closeViewModal = () => setViewEvent(null);


  return (
    <div className="admin-events-page">
      {/* HEADER */}
      <header className="portal-header">
        <div
          className="header-logo-group"
          onClick={() => navigate("/admin/dashboard")}
          style={{ cursor: "pointer" }}
        >
          <img src={logoSvg} alt="Logo" className="portal-logo-svg" />
        </div>
        <button className="back-link-text" onClick={() => navigate("/admin/dashboard")}>
          Voltar ao Menu
        </button>
      </header>

      <div className="events-container">
        <h2 className="header-title">Gestão de Eventos</h2>

        {loading ? (
          <div className="loading-state">
            <div className="loader" />
            <p>Carregando eventos...</p>
          </div>
        ) : (
          <>
            {/* TABS */}
            <div className="tabs-container">
              {STATUS_TABS.map(tab => (
                <button
                  key={tab.key}
                  className={`tab-btn ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  <span className="tab-count">
                    ({Array.isArray(events)
                      ? events.filter(e => (e.status || "Pendente") === tab.key).length
                      : 0})
                  </span>
                </button>
              ))}
            </div>

            {/* LIST */}
            <main className="events-list">
              {filteredEvents.length === 0 ? (
                <div className="empty-state">
                  Nenhum evento {activeTab.toLowerCase()} encontrado.
                </div>
              ) : (
                filteredEvents.map(event => (
                  <div
                    key={event.id}
                    className="event-card"
                    onClick={() => setViewEvent(event)}
                  >
                    <div className={`card-accent-line accent-${normalizeStatus(activeTab)}`} />

                    <div className="card-content">
                      <div className="card-header-row">
                        <span className="event-type-label">{event.event_type}</span>
                        <span className={`status-pill status-${normalizeStatus(event.status)}`}>
                          {event.status || "Pendente"}
                        </span>
                      </div>

                      <h2 className="event-title-text">{event.event_name}</h2>

                      <div className="info-row">
                        <span>📍 {event.location}</span>
                        <span>👤 {event.requester_name}</span>
                      </div>

                      <div className="event-datetime-box">
                        📅 {new Date(event.start_datetime).toLocaleString("pt-BR")}
                      </div>

                      <div className="card-footer-admin">
                        <span>
                          {event.technician
                            ? `Técnico: ${event.technician}`
                            : "Aguardando técnico"}
                        </span>

                        <div className="card-actions">
                          <button
                            className="btn-update"
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedEvent(event);
                              setTechnician(event.technician || "");
                              setStatus(event.status || "Aprovado");
                            }}
                          >
                            Atualizar
                          </button>

                          <button
                            className="btn-remove"
                            onClick={e => {
                              e.stopPropagation();
                              handleDelete(event.id);
                            }}
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </main>
          </>
        )}
      </div>

      {/* MODAL VISUALIZAR - CORRIGIDO PARA O SEU JSON */}
      {viewEvent && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-box modal-view" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">{viewEvent.event_name}</h2>
            <div className="modal-scroll-content">
                <p><strong>Tipo:</strong> {viewEvent.event_type}</p>
                <p><strong>Local:</strong> {viewEvent.location}</p>
                <p><strong>Solicitante:</strong> {viewEvent.requester_name} ({viewEvent.sector})</p>
                <p><strong>Horário:</strong> {new Date(viewEvent.start_datetime).toLocaleString()}</p>
                
                <div className="modal-desc-section">
                    <strong>Recursos Solicitados:</strong>
                    <div className="resources-container-modal">
                      {(() => {
                        try {
                          const resList = typeof viewEvent.resources === 'string' 
                            ? JSON.parse(viewEvent.resources) 
                            : viewEvent.resources;
                          
                          if (!resList || resList.length === 0) return <span className="no-data-text">Nenhum</span>;
                          
                          return RESOURCES_MAP
                            .filter(r => resList.includes(r.id))
                            .map(r => <span key={r.id} className="resource-tag-view">{r.label}</span>);
                        } catch {
                          return <span className="no-data-text">Nenhum</span>;
                        }
                      })()}
                    </div>
                </div>

                {viewEvent.description && (
                    <div className="modal-desc-section">
                        <strong>Descrição:</strong>
                        <div className="desc-text-wrapper">{viewEvent.description}</div>
                    </div>
                )}
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeViewModal}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ATUALIZAR STATUS */}
      {selectedEvent && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2 className="modal-title">Atualizar Evento</h2>
            <div className="form-group">
              <label>Técnico Responsável</label>
              <input
                className="modal-input"
                value={technician}
                onChange={(e) => setTechnician(e.target.value)}
                placeholder="Nome do técnico"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select className="modal-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_TABS.map(tab => (
                  <option key={tab.key} value={tab.key}>{tab.key}</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button className="confirm-btn" onClick={handleUpdateEvent}>Salvar</button>
              <button className="cancel-btn" onClick={() => setSelectedEvent(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* POPUP DE ERRO */}
      {showError && (
        <div className="error-overlay" style={{ zIndex: 9999 }}>
          <div className="error-popup-card">
            <div className="error-icon-circle">!</div>
            <h3>Atenção</h3>
            <p>{errorMessage}</p>
            <button className="error-close-btn" onClick={() => setShowError(false)}>Entendido</button>
          </div>
        </div>
      )}
    </div>
  );
}